import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { ACCOUNT_ENTRY_PATH } from 'dashboard/constants/globals';
import {
  hasFeatureFlagRule,
  isFeatureFlagRuleEnabled,
} from './featureFlagRule';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: ACCOUNT_ENTRY_PATH,
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : ACCOUNT_ENTRY_PATH}`;
};

// Rota com meta.featureFlag desligado na conta é negada, também por URL
// direta. Enquanto as features da conta não carregaram (sem o objeto
// features), a rota passa: o router confere de novo quando elas chegam.
// featureFlag pode ser uma lista (o SLA pede reports e sla): a rota só abre
// com todas ligadas. Ou { any: [...] } (Captain antigo ou V2): basta uma.
// A regra é a mesma de accounts/isFeatureEnabledonAccount (featureFlagRule).
export const routeFeatureFlagBlocked = (route, accountFeatures) => {
  const flag = route?.meta?.featureFlag;
  if (!hasFeatureFlagRule(flag)) return false;
  if (!accountFeatures || typeof accountFeatures !== 'object') return false;
  return !isFeatureFlagRuleEnabled(flag, accountFeatures);
};

const validateActiveAccountRoutes = (to, user, accountFeatures) => {
  // If the current account is active, then check for the route permissions
  const accountEntryURL = `accounts/${to.params.accountId}/${ACCOUNT_ENTRY_PATH}`;

  // If the user is trying to access suspended route, redirect them to the entry
  if (to.name === 'account_suspended') {
    return accountEntryURL;
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  if (!isAccessible) return defaultRedirectPage(to, userPermissions);

  if (routeFeatureFlagBlocked(to, accountFeatures)) {
    const target = defaultRedirectPage(to, userPermissions);
    // Se a página padrão é a própria rota, não há para onde mandar a
    // pessoa sem entrar em laço: ela fica onde está.
    return to.path?.endsWith(`/${target}`) ? null : target;
  }

  return null;
};

export const validateLoggedInRoutes = (to, user, accountFeatures) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user, accountFeatures);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
    // SiteUp: lista de não lidas também é lista de conversa.
    'conversation_unread',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
    'conversation_through_unread',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    case 'conversation_through_unread':
      return 'conversation_unread';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
